import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Bell, Check, ArrowRight, Sparkles } from "lucide-react";

const PhoneMockup = () => (
  <div className="relative mx-auto w-[270px] sm:w-[300px]">
    <div className="absolute -left-10 top-10 animate-float-slow">
      <div className="rounded-2xl bg-brand-green px-3 py-2 shadow-lg text-white text-xs font-semibold rotate-[-6deg]">
        +20% visite 🎉
      </div>
    </div>
    <div className="absolute -right-8 top-1/3 animate-float">
      <div className="rounded-2xl bg-white px-3 py-2 shadow-lg text-brand-deep text-xs font-semibold rotate-[6deg] border border-gray-100">
        Nuovo iscritto!
      </div>
    </div>

    <div className="relative rounded-[2.6rem] border-[10px] border-brand-deep bg-brand-deep shadow-2xl shadow-brand-deep/30">
      <div className="rounded-[2rem] bg-gradient-to-br from-brand to-brand-green overflow-hidden h-[560px] relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-brand-deep rounded-b-2xl" />
        <div className="pt-12 px-5 text-white">
          <p className="text-sm/none opacity-80">9:41</p>
          <p className="mt-1 text-lg font-heading font-bold">Martedì 21 Maggio</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mx-4 mt-6 rounded-2xl bg-white/95 backdrop-blur p-4 shadow-xl"
        >
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand text-white">
              <Bell size={16} strokeWidth={2.5} />
            </span>
            <span className="font-heading font-bold text-brand-deep text-sm">PushApp · ora</span>
          </div>
          <p className="mt-2 font-body text-sm text-ink leading-snug">
            🎁 Offerta speciale solo per te! <b>Sconto del 20%</b> valido oggi.
          </p>
        </motion.div>

        <div className="mx-4 mt-4 rounded-2xl bg-white/85 p-4 shadow-lg">
          <p className="font-heading font-bold text-brand-deep text-sm">Nuova collezione 👗</p>
          <p className="font-body text-xs text-ink mt-1">Vieni in anteprima — solo iscritti, fino a domenica.</p>
        </div>

        <div className="absolute bottom-5 left-0 right-0 flex justify-center">
          <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-lg">
            <Check size={14} className="text-brand-green" strokeWidth={3} />
            <span className="text-xs font-semibold text-brand-deep">Aggiunto alla home</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const HomeHero = () => {
  return (
    <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-light/40 blur-3xl" />
        <div className="absolute top-1/2 -left-32 h-80 w-80 rounded-full bg-brand-green/15 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-light/40 px-4 py-1.5 text-sm font-bold uppercase tracking-[0.15em] text-brand">
            <Sparkles size={15} strokeWidth={2.5} /> La tua web app personalizzata
          </span>

          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-brand-deep leading-[1.05]">
            Il tuo canale diretto con i clienti.
            <span className="block text-brand">Semplice. Immediato. Sempre attivo.</span>
          </h1>

          <p className="mt-6 font-body text-base sm:text-lg text-ink leading-relaxed max-w-xl">
            PushApp è la web app su misura per la tua attività: invii notifiche, news,
            eventi e promozioni che arrivano dritti sullo smartphone dei tuoi clienti.
            <b className="text-brand-deep"> Nessun download dagli store</b> — basta un link.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/assistenza"
              data-testid="hero-cta-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-body font-semibold text-white shadow-lg shadow-brand/30 transition-all duration-300 hover:bg-brand-hover hover:-translate-y-1"
            >
              Richiedi una demo gratuita <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
            <Link
              to="/guide"
              data-testid="hero-secondary-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-light/30 px-8 py-4 font-body font-semibold text-brand transition-all duration-300 hover:bg-brand-light/50"
            >
              Hai già l'app? Vai alle guide
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-ink">
            <div className="flex items-center gap-2"><Check size={16} className="text-brand-green" strokeWidth={3} /> Pronta in pochi giorni</div>
            <div className="flex items-center gap-2"><Check size={16} className="text-brand-green" strokeWidth={3} /> Canone fisso, zero commissioni</div>
            <div className="flex items-center gap-2"><Check size={16} className="text-brand-green" strokeWidth={3} /> Database di tua proprietà</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="relative">
          <PhoneMockup />
        </motion.div>
      </div>
    </section>
  );
};
